import React, { useEffect, useRef, useState } from "react";
import ContactForm from "../ContactFrom/ContactForm";

const stats = [
  { value: 30,   suffix: " min",  label: "Average API integration time" },
  { value: 99.9, suffix: "%",     label: "Uptime SLA on all API plans" },
  { value: 500,  suffix: "+",     label: "Companies trust TrustEngine" },
];

const features = [
  "eKYC & Face Biometric",
  "Watchlist & AML Screening",
  "Bank Statement Analyzer",
  "ISO 27001 Certified",
  "OJK & GDPR Compliant",
  "Dedicated Support",
];

const ContactSection: React.FC = () => {
  const [counts, setCounts] = useState([0, 0, 0]);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      stats.forEach((stat, i) => {
        const steps = 40;
        const increment = stat.value / steps;
        let current = 0;
        const interval = setInterval(() => {
          current = Math.min(current + increment, stat.value);
          setCounts(prev => { const n = [...prev]; n[i] = current; return n; });
          if (current >= stat.value) clearInterval(interval);
        }, 40);
      });
      observer.disconnect();
    }, { threshold: 0.3 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="contact-section pt-150 pb-140" ref={sectionRef}>
      <div className="container">
        <div className="row mt-none-50 align-items-center">

          {/* ── Left side ── */}
          <div className="col-lg-6 col-12 mt-50 contact-left-col" style={{ paddingRight: "40px" }}>
            <div className="sec-title contact-sec-title mb-40">
              <span className="sub-title mb-15">Get Started</span>
              <h2 className="title">Start verifying identities today</h2>
              <p style={{ color: "#374151", marginTop: "16px", lineHeight: "1.7", fontSize: "16px" }}>
                Enterprise-grade identity verification API — integrate in minutes,
                scale to millions. No long-term commitment required.
              </p>
            </div>

            {/* Stat tiles */}
            <div className="contact-stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "12px", marginBottom: "36px" }}>
              {stats.map((stat, i) => (
                <div key={i} style={{
                  flex: 1, background: "#ffffff",
                  border: "1px solid rgba(15,32,64,0.1)",
                  borderRadius: "14px", padding: "20px 16px",
                  boxShadow: "0 2px 12px rgba(15,32,64,0.06)",
                  textAlign: "center",
                }}>
                  <div style={{ fontSize: "28px", fontWeight: 800, color: "#0f2040", lineHeight: 1 }}>
                    {stat.suffix === "%" ? counts[i].toFixed(1) : Math.round(counts[i])}{stat.suffix}
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px", lineHeight: 1.4 }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Feature checklist */}
            <div className="contact-features-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {features.map((f, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{
                    width: "18px", height: "18px", borderRadius: "50%",
                    background: "rgba(0,200,150,0.12)", border: "1px solid rgba(0,200,150,0.3)",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
                      <path d="M1.5 4.5l2 2 4-4" stroke="#00c896" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>{f}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right side: Form ── */}
          <div className="col-lg-6 col-12 mt-50">
            <div className="xb-contact-form xb-border" style={{
              background: "#ffffff",
              borderRadius: "20px",
              padding: "36px",
              boxShadow: "0 8px 40px rgba(15,32,64,0.09)",
              border: "1px solid rgba(15,32,64,0.08)",
            }}>
              <div className="form-heading text-center mb-30">
                <h3 className="title" style={{ fontSize: "22px", color: "#0f2040" }}>Request your API key</h3>
                <p style={{ color: "#6b7280", marginTop: "6px", fontSize: "14px" }}>
                  Go live in minutes. No commitment required.
                </p>
              </div>
              <ContactForm />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactSection;
